import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Image as ImageIcon, Upload, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const { token } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', image_url: '', category: 'general', description: '' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try { const res = await axios.get(`${API_URL}/api/gallery`); setGallery(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ title: '', image_url: '', category: 'general', description: '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile && !formData.image_url) { toast.error('Please select an image'); return; }
    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let submitData = { ...formData };

      if (imageFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', imageFile);
        const uploadRes = await axios.post(
          `${API_URL}/api/uploads?file_type=gallery`,
          uploadForm,
          { headers }
        );
        submitData = { ...submitData, image_url: uploadRes.data.file_url.replace(/^https?:\/\/[^/]+/, '') };
      }

      await axios.post(`${API_URL}/api/gallery`, submitData, { headers });
      toast.success('Image added!');
      setDialogOpen(false);
      resetForm();
      fetchGallery();
    } catch (e) {
      toast.error('Failed to add');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try { await axios.delete(`${API_URL}/api/gallery/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted!'); fetchGallery(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <AdminLayout title="Gallery">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage gallery images.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-gallery-btn"><Plus className="w-4 h-4 mr-2" /> Add Image</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Gallery Image</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></div>
              <div className="space-y-2">
                <Label>Image</Label>
                <div
                  className="border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
                  onClick={() => document.getElementById('gallery-image-input').click()}
                >
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover" />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setImageFile(null); setImagePreview(null); }}
                        className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        {imageFile?.name}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm font-medium">Click to upload image</span>
                      <span className="text-xs">JPG, PNG, GIF supported</span>
                    </div>
                  )}
                </div>
                <input
                  id="gallery-image-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="space-y-2"><Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['general', 'campus', 'classroom', 'events', 'results', 'seminars'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Uploading...
                  </span>
                ) : 'Add Image'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (<div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1, 2, 3, 4].map(i => <div key={i} className="aspect-square bg-slate-200 rounded-xl animate-pulse"></div>)}</div>) : gallery.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {gallery.map((item) => (
            <div key={item.id} className="relative group" data-testid={`gallery-${item.id}`}>
              <div className="aspect-square rounded-xl overflow-hidden bg-slate-100">
                <img src={item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url.startsWith('/') ? '' : '/'}${item.image_url}`} alt={item.title} className="w-full h-full object-cover" onError={(e) => e.target.src = 'https://via.placeholder.com/300'} />
              </div>
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <Button variant="destructive" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="mt-2"><p className="font-medium text-sm truncate">{item.title}</p><Badge variant="outline" className="text-xs">{item.category}</Badge></div>
            </div>
          ))}
        </div>
      ) : (<Card><CardContent className="py-16 text-center"><ImageIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No Images</h3></CardContent></Card>)}
    </AdminLayout>
  );
};

export default AdminGallery;
